import { memo, useRef, useState } from 'react';
import { AnswerIcon, WarningIcon } from '@/sub-luca-plugin/compatible-components/svg-icons';
import ProjectFilter from '../my-collect-render/project-filter';
import Skeleton from './skeleton';
import { questions_item_detail, read_message } from '@/sub-luca-plugin/apis';
import { query_questions } from '@/sub-luca-plugin/apis/my-question';
import { IProjectIds, IMyQuestionItem, IMyQuestionProps } from '@/sub-luca-plugin/types';
import { IScrollViewContext, ScrollView } from '@/sub-luca-plugin/compatible-components';
import { navigateExample } from '@/sub-luca-plugin/utils/class-example';
import { useNewestData } from '@/sub-luca-plugin/utils/custom-hooks';
import classNames from 'classnames';
import './index.scss';

const MyQuestionRender = (props: IMyQuestionProps) => {
    const projctParams = useRef<any>({
        projectId: props.projectId && +props.projectId,
        subjectId: props.subjectId && +props.subjectId,
        twoLevelSubjectId: props.twoLevelSubjectId && +props.twoLevelSubjectId,
    });
    const basicInfo = useRef({
        sourceMark: props.sourceMark || 'gaodun',
        entrySource: props.entrySource || 'gaodun-pc',
        clientSource: props.clientSource || '100537565',
    });
    const scrollViewContext = useRef<IScrollViewContext>();
    const [initLoading, setInitLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [totalNum, setTotal] = useState(100);
    const [myQuestionList, setMyQuestionList] = useState<IMyQuestionItem[]>([]);
    const [currentCommIndex, setCurrentCommIndex] = useState<number>();

    const hasQuestionList = !!myQuestionList.length;

    const newestInitLoading = useNewestData(initLoading);
    const newestLoading = useNewestData(loading);

    // 提问列表
    const initMyQuestionList = async ({ pageNum, projectId, subjectId, twoLevelSubjectId }: any) => {
        pageNum === 1 ? setInitLoading(true) : setLoading(true);
        const { result } = await query_questions({
            projectId: projectId === -2 ? undefined : projectId,
            subjectId: subjectId === -2 ? undefined : subjectId,
            twoLevelSubjectId: twoLevelSubjectId === -2 ? undefined : twoLevelSubjectId,
            pageSize: props?.pageSize || 20,
            pageNum,
        });
        pageNum === 1 ? setInitLoading(false) : setLoading(false);
        const { totalSize = 0, pageNum: pageNumber = 1, list = [] } = result;
        setTotal(totalSize);
        setPageNum(pageNumber);
        if (pageNum === 1) {
            setMyQuestionList(list);
            scrollViewContext.current?.scrollToTop();
        } else {
            setMyQuestionList([...myQuestionList, ...result.list]);
        }
    };

    // 分页
    const onScrollToLower = () => {
        if (myQuestionList.length >= totalNum || newestInitLoading.current || newestLoading.current) return;
        initMyQuestionList({ pageNum: pageNum + 1, ...projctParams.current });
    };

    // 项目变更
    const handleProjectChange = (params: IProjectIds) => {
        projctParams.current = params;
        initMyQuestionList({ pageNum: 1, ...projctParams.current });
    };

    const updateItem = async (id: number) => {
        const { result } = await questions_item_detail(id);
        if (result) {
            setMyQuestionList(
                myQuestionList.map((item: any) => {
                    if (item.id === id) return result;
                    return item;
                })
            );
        }
    };

    const handleClick = (item: IMyQuestionItem, index: number) => {
        setCurrentCommIndex(index);
        read_message(item.id);
        navigateExample.linkTo(
            'qa',
            {
                ...basicInfo.current,
                lucaAskChatId: item.lucaAskChatId,
                askId: item.askId,
                showCreateNewQaBtn: false,
                from: 'my_question',
            },
            {
                onClose: () => {
                    setCurrentCommIndex(undefined);
                    updateItem(item.id);
                },
            }
        );
    };

    return (
        <div className="my-question">
            {initLoading && <Skeleton />}
            <ProjectFilter type="my_question" initIds={projctParams.current} onChange={handleProjectChange} />
            <div className="my-question-list-container">
                {!hasQuestionList && (
                    <div className="my-question__empty-container">
                        <img src="http://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202403/12/3e82d_20240312135601.png" />
                    </div>
                )}
                {hasQuestionList && (
                    <ScrollView
                        ref={scrollViewContext}
                        className="my-question-list-wrap"
                        scrollY
                        holdingPosition
                        lowerThreshold={100}
                        onScrollToLower={onScrollToLower}
                    >
                        <div className="my-question-list">
                            {myQuestionList.map((item, index) => (
                                <div
                                    key={item.id}
                                    className={classNames('my-question-item', currentCommIndex === index && 'my-question-item--opening')}
                                    onClick={() => handleClick(item, index)}
                                >
                                    <AnswerIcon />
                                    <div className="my-question-item__info">
                                        <div className="answer-content text-row-overflow">
                                            {item.askChatTitle ? item.askChatTitle : <span style={{ color: '#a5a5a5' }}>暂无提问主题</span>}
                                        </div>

                                        <div className="flex-box">
                                            <div className="answer-status">
                                                {item.transferLaborState === 1 && <div className="famours-teacher">名师</div>}
                                                {item.questionState === 4 && item.transferLaborState === 1 && (
                                                    <div className="comments-status">待回答</div>
                                                )}
                                                {item.questionState === 2 && <div className="satisfied">已解决</div>}

                                                {item.transferLaborState === 1 && item.questionState === 5 && item.appraiseState === 0 && (
                                                    <div className="comments-status">待评价</div>
                                                )}

                                                {item.transferLaborState === 1 && item.appraiseState === 1 && (
                                                    <div className="satisfied">已评价</div>
                                                )}
                                            </div>
                                            <div className="answer-time">{item.createTime}</div>
                                        </div>

                                        {item.readState === 1 && (
                                            <div className="message-tip">
                                                <WarningIcon className="anticon" />
                                                <span>你有一条新回答</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && <div className="my-question-list__loading">加载中...</div>}
                            {myQuestionList.length >= totalNum && <div className="no-more">没有更多了～</div>}
                        </div>
                    </ScrollView>
                )}
            </div>
        </div>
    );
};

export default memo(MyQuestionRender);
